export const ENDPOINTS = {
  REGISTER_ORGANISATION: '/registration/organisation',
  RESEND_VERIFICATION: '/registration/resend-verification',
  UPGRADE_REQUEST: '/upgrade-request',
  WORKSPACE_LIST: '/workspace/list',
  PARSE_DOCUMENT: '/admin/parse-document',
  QUESTION_EDITING: '/admin/question-editing',
  LEVEL_CREATION: '/admin/level-creation',
  COURSE_ANALYSIS: '/admin/course-analysis',
  COURSE_ANALYSIS_INITIALIZE: '/admin/course-analysis/initialize',
  COURSE_ANALYSIS_SAVE: '/admin/course-analysis/save',
  QUESTION_GENERATION: '/admin/question-generation',
  USER_LIST: (
    organisationId: number,
    page: number,
    limit: number,
    role?: string,
    status?: string,
    search?: string,
    departmentIds?: number[],
  ) => {
    const params = new URLSearchParams({
      organisationId: organisationId.toString(),
      page: page.toString(),
      limit: limit.toString(),
    });
    if (role && role !== 'all') params.append('role', role);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (departmentIds && departmentIds.length > 0) {
      departmentIds.forEach((id) =>
        params.append('departmentIds', id.toString()),
      );
    }
    return `/admin/user/list?${params.toString()}`;
  },
  USER_GET: (userId: number, organisationId: number) =>
    `/admin/user/${userId}?organisationId=${organisationId}`,
  USER_CREATE: '/admin/user/create',
  USER_BULK_IMPORT: '/admin/user/bulk-import',
  USER_UPDATE: '/admin/user/update',
  USER_PERMANENTLY_DELETE: (userId: number, organisationId: number) =>
    `/admin/user/${userId}?organisationId=${organisationId}`,
  USER_STATS_OVERVIEW: (userId: number, organisationId: number) =>
    `/admin/user/${userId}/stats?organisationId=${organisationId}`,
  USER_STATS_ACTIVITY: (
    userId: number,
    organisationId: number,
    page: number,
    limit: number,
  ) =>
    `/admin/user/${userId}/activity?organisationId=${organisationId}&page=${page}&limit=${limit}`,
  USER_STATS_COURSES: (
    userId: number,
    organisationId: number,
    page: number,
    limit: number,
    search?: string,
  ) =>
    `/admin/user/${userId}/courses?organisationId=${organisationId}&page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  USER_STATS_TESTS: (
    userId: number,
    organisationId: number,
    page: number,
    limit: number,
  ) =>
    `/admin/user/${userId}/tests?organisationId=${organisationId}&page=${page}&limit=${limit}`,
  COURSE_LIST: (
    organisationId: number,
    page: number,
    limit: number,
    status?: string,
    search?: string,
  ) => {
    const params = new URLSearchParams({
      organisationId: organisationId.toString(),
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status && status !== 'all') params.append('status', status);
    if (search) params.append('search', search);
    return `/admin/course/list?${params.toString()}`;
  },
  COURSE_GET: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}?organisationId=${organisationId}`,
  COURSE_HAS_LEVELS: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}/has-levels?organisationId=${organisationId}`,
  COURSE_TOGGLE_STATUS: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}/status?organisationId=${organisationId}`,
  COURSE_TOGGLE_RANDOMIZE: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}/randomize?organisationId=${organisationId}`,
  COURSE_UPDATE: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}?organisationId=${organisationId}`,
  COURSE_CREATE: '/admin/course/create',
  COURSE_DELETE: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}?organisationId=${organisationId}`,
  COURSE_DUPLICATE: (courseId: number, organisationId: number) =>
    `/admin/course/${courseId}/duplicate?organisationId=${organisationId}`,
  ORGANIZATION_SETTINGS: (organizationId: number) =>
    `/admin/organization/${organizationId}/settings`,
  ORGANIZATION_STATS_OVERVIEW: (organizationId: number) =>
    `/admin/organization/${organizationId}/stats`,
  ORGANIZATION_STATS_ACTIVITY: (
    organizationId: number,
    page: number,
    limit: number,
  ) =>
    `/admin/organization/${organizationId}/stats/activity?page=${page}&limit=${limit}`,
  ORGANIZATION_STATS_COURSES: (
    organizationId: number,
    page: number,
    limit: number,
  ) =>
    `/admin/organization/${organizationId}/stats/courses?page=${page}&limit=${limit}`,
  ORGANIZATION_STATS_PEOPLE: (
    organizationId: number,
    page: number,
    limit: number,
  ) =>
    `/admin/organization/${organizationId}/stats/people?page=${page}&limit=${limit}`,
  ORGANIZATION_STATS_POINTS: (
    organizationId: number,
    page: number,
    limit: number,
    includeTests: boolean,
    startDate?: string,
    endDate?: string,
    courseIds?: number[],
    departmentIds?: number[],
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      includeTests: includeTests.toString(),
    });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    (courseIds ?? []).forEach((courseId) =>
      params.append('courseIds', courseId.toString()),
    );
    (departmentIds ?? []).forEach((departmentId) =>
      params.append('departmentIds', departmentId.toString()),
    );
    return `/admin/organization/${organizationId}/stats/points?${params.toString()}`;
  },
  ORGANIZATION_STATS_TESTS: (
    organizationId: number,
    page: number,
    limit: number,
    startDate: string,
    endDate: string,
    departmentIds?: number[],
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      startDate,
      endDate,
    });
    (departmentIds ?? []).forEach((departmentId) =>
      params.append('departmentIds', departmentId.toString()),
    );
    return `/admin/organization/${organizationId}/stats/tests?${params.toString()}`;
  },
  ORGANIZATION_STATS_MEMBER_TESTS: (
    organizationId: number,
    userId: number,
    startDate: string,
    endDate: string,
  ) => {
    const params = new URLSearchParams({ startDate, endDate });
    return `/admin/organization/${organizationId}/stats/tests/${userId}?${params.toString()}`;
  },
  ORGANIZATION_STATS_TOP_PERFORMERS: (
    organizationId: number,
    limit: number,
    includeTests: boolean,
    startDate?: string,
    endDate?: string,
  ) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      includeTests: includeTests.toString(),
    });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return `/admin/organization/${organizationId}/stats/top-performers?${params.toString()}`;
  },
  ORGANIZATION_STATS_COURSE_DETAIL: (
    organizationId: number,
    courseId: number,
  ) => `/admin/organization/${organizationId}/stats/courses/${courseId}`,
  ORGANIZATION_STATS_PERSON_DETAIL: (organizationId: number, userId: number) =>
    `/admin/organization/${organizationId}/stats/people/${userId}`,
  DEPARTMENT_LIST: '/admin/department/list',
  DEPARTMENT_CREATE: '/admin/department/create',
  DEPARTMENT_UPDATE: (departmentId: number) =>
    `/admin/department/${departmentId}`,
  DEPARTMENT_DELETE: (departmentId: number, organisationId: number) =>
    `/admin/department/${departmentId}?organisationId=${organisationId}`,
  NOTIFICATION_CREATE: '/admin/notification',
  NOTIFICATION_SETTINGS: (organisationId: number) =>
    `/admin/notification/settings?organisationId=${organisationId}`,
};
